<template>
  <app-page-gradient-background class="min-h-screen !pt-0">
    <app-page-wrapper
      class="flex h-[60px] items-center justify-between md:h-[80px]"
      tag="header"
    >
      <nuxt-link to="/">
        <brand-navigation-logo />
      </nuxt-link>

      <app-button-back v-if="previous" :to="previous" />
    </app-page-wrapper>

    <app-page-wrapper class="!px-6 pb-24 md:pb-0">
      <div class="grid gap-4 lg:grid-cols-[1fr_450px] xl:grid-cols-[1fr_550px]">
        <div
          class="from-primary-950 to-primary-900 flex flex-col justify-center rounded-lg bg-gradient-to-t to-[70%] px-6 py-6 md:px-8 lg:px-12"
        >
          <component :is="component" class="text-white" :metadata="metadata" />
        </div>

        <div class="rounded-lg bg-white p-6 shadow-lg md:p-8">
          <contact-page-form
            :options="bindings?.(metadata)?.form"
            v-bind="metadata"
            :previous="previous"
            :redirect="redirect"
            @submit="onSubmit"
          />
        </div>
      </div>
    </app-page-wrapper>

    <app-page-wrapper class="mt-16 hidden flex-col gap-16 md:flex">
      <brand-logos-carousel
        :items="[
          'akur8',
          'pennylane',
          'loreal',
          'doctolib',
          'carrefour',
          'docaposte',
          'morning',
          'orange',
          'malt',
          'jus-mundi',
          'bnpp',
          'deezer',
        ]"
      >
        <template #title>
          <span class="mr-2" v-html="$t('brand.rating.average_label')" />
          <app-rating-stars
            class="inline-block align-text-bottom"
            :value="4.5"
          />
        </template>
      </brand-logos-carousel>

      <brand-reassurance-figures />

      <brand-testimonial-card-list :list="testimonials" />

      <p class="text-subtle mb-8 text-center text-sm">
        {{ copyrightMention }}
      </p>
    </app-page-wrapper>
  </app-page-gradient-background>
</template>

<script lang="ts" setup>
import { useCopyrightMention } from "~/layouts/_includes/composables/main-footer.hook";
import ContactPageForm from "~/pages/contact/_includes/components/contact-page-form.vue";
import { useContactPageResolver } from "~/pages/contact/_includes/composables/contact-page-resolver.hook";
import type { ContactFlowMetadata } from "~~/model/analytics.model";

definePageMeta({
  layout: false,
});

const { t } = useI18n();

useSeoMeta({
  title: t("growth.contact_page.title"),
});

const copyrightMention = useCopyrightMention();

const route = useRoute();

const metadata = computed<ContactFlowMetadata>(() => {
  const query = route.query as ContactFlowMetadata;

  return {
    origin: query.origin,
    origin_page_id: query.origin_page_id,
    origin_page_action: query.origin_page_action,
  };
});

const { component, bindings } = await useContactPageResolver(metadata);

const previous = usePreviousRoute();

const redirect = computed(() => {
  if (route.query.redirect) return route.query.redirect;
  if (route.name === previous?.name) return "/";

  return previous?.fullPath ?? "/";
});

function onSubmit() {
  if (
    route.query.confirmation_toast &&
    route.query.confirmation_toast !== "true"
  )
    return;

  showSuccessToastNotification(t("growth.contact_page.confirmation.title"), {
    subtitle: t("growth.contact_page.confirmation.subtitle"),
    icon: "ph:paper-plane-tilt",
  });
}

const { data: testimonials } = useTestimonialList();
</script>
